import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import './InvestorQualification.scss';
import { Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { Button, FormGroup, Label } from 'reactstrap';
import * as Yup from 'yup';
import LaddaButton, { ZOOM_OUT } from 'react-ladda';
import { useHistory, useParams } from 'react-router-dom';
import SecuritizePage from '../../../components/SecuritizePage';
import { getQualificationQuestions, submitQualificationQuestions } from './actions';
import Translate from '../../../i18n/Translate/Translate';
import Radio from './components/Radio';
import ReadMore from '../../../components/ReadMore/ReadMore';
import { showModal } from '../../../utils/helpers/actions';
import CustomCircleProgressBar from '../Investment/components/CustomCircleProgressBar';
import SanitizedReactHtmlParser from '../../../utils/helpers/SanitizedReactHtmlParser';

const ErrorModalDisplay = ({ showModal }) => {
  const { errors, touched } = useFormikContext();
  const has = (obj) => !!Object.keys(obj).length;
  useEffect(() => {
    if (has(errors) && has(touched)) {
      showModal('QualificationRequired');
    }
  }, [errors, touched]);

  return null;
};

ErrorModalDisplay.propTypes = {
  showModal: PropTypes.func.isRequired,
};

const InvestorQualification = (props) => {
  const {
    isError, isLoading, sections, getQualificationQuestions, onSubmitSuccess, submitQualificationQuestions, onSubmitError,
    showModal, user, pageNumber, routesLength,
  } = props;
  const history = useHistory();
  const { id: opportunityId } = useParams();

  useEffect(() => {
    if (user.isAccredited) {
      history.push('/home');
    } else if (!isLoading) {
      getQualificationQuestions(opportunityId);
    }
  }, [opportunityId]);

  const questions = sections.reduce((questions, section) => [...questions, ...section.questions], []);

  const buttonQuestion = questions.find(({ type }) => type === 'button');
  const hasCheckBoxQuestions = questions.find(({ type }) => type === 'checkbox');

  const schema = Yup.object().shape({
    ...questions.reduce((schema, question) => {
      const { valid, id } = question;
      schema[id] = Yup.string().oneOf(valid).required();
      return schema;
    }, {}),
  });

  const initialValues = questions.reduce((initialValues, question) => {
    initialValues[question.id] = '';
    return initialValues;
  }, {});

  return (
    <SecuritizePage
      isLoading={isLoading}
      isError={isError}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        validateOnMount
        onSubmit={(values, formikBag) => {
          formikBag.setSubmitting(true);
          const answers = Object.entries(values).reduce((answersArray, question) => {
            answersArray.push({
              id: question[0], // question id
              selectedOption: question[1], // selected option
            });
            return answersArray;
          }, []);
          submitQualificationQuestions(answers, opportunityId, (error, res) => {
            if (error) {
              onSubmitError(error);
            } else {
              onSubmitSuccess(res);
            }
            formikBag.setSubmitting(false);
          });
        }}
      >
        {(formikBag) => {
          const {
            values, handleChange, handleBlur, errors, touched, isSubmitting,
          } = formikBag;
          const sections2 = errors[(buttonQuestion || {}).id] ? [sections[0]] : sections.slice(1);
          return (
            <SecuritizePage isLoading={isSubmitting || isLoading}>
              <div className="animated fadeIn investor-qualification">
                <div className="investor-qualification__title-container">
                  {
                    !!pageNumber && (
                      <CustomCircleProgressBar
                        currentValue={pageNumber}
                        totalItems={routesLength}
                      />
                    )
                  }
                  <p className="font-32">
                    {
                      !!pageNumber && (
                        <span>
                          {`0${pageNumber}. `}
                        </span>
                      )
                    }
                    <span>
                      {SanitizedReactHtmlParser(sections2[0] && sections2[0].sectionTitle)}
                    </span>
                  </p>
                </div>
                {
                  sections2.map(({ sectionSubTitle, questions: sectionQuestions }) => (
                    <div className="investor-qualification__section" key={sectionSubTitle}>
                      <p className="investor-qualification__section-sub-title font-18 subtext mb-4">{sectionSubTitle}</p>
                      {
                        sectionQuestions.map((question) => (
                          <FormGroup key={question.id + question.title}>
                            {
                              hasCheckBoxQuestions && (
                                <>
                                  <Label className="font-16">
                                    <Translate translationKey={question.title} />
                                  </Label>
                                  <div className="investor-qualification__section__radio-group">
                                    {
                                      question.options.map((option) => (question.type === 'checkbox' ? (
                                        <Radio
                                          className="investor-qualification__section__radio-group__radio mt-3"
                                          key={option}
                                          name={question.id}
                                          id={question.id + option}
                                          invalid={(touched[question.id] && !!errors[question.id])}
                                          onBlur={handleBlur}
                                          onChange={handleChange}
                                          value={option}
                                          checked={option === values[question.id]}
                                        />
                                      ) : null))
                                    }
                                  </div>
                                </>
                              )
                            }
                            {
                              buttonQuestion && errors[(buttonQuestion || {}).id] && (
                                <>
                                  <ErrorModalDisplay showModal={showModal} />
                                  <ReadMore
                                    maxWidth="100%"
                                    lines={10}
                                    fontSize={16}
                                  >
                                    <Label className="font-16 investor-qualification__section__label">
                                      {SanitizedReactHtmlParser(question.title)}
                                    </Label>
                                  </ReadMore>
                                  <div className="d-flex pt-3 mt-5 mb-5">
                                    {
                                      question.options.map((option, index) => (question.type === 'button' ? (
                                        <Button
                                          id={`btn-question-option-${index + 1}`}
                                          key={option}
                                          name={question.id}
                                          onBlur={handleBlur}
                                          onClick={() => {
                                            formikBag.setFieldValue(question.id, option);
                                            window.setTimeout(formikBag.handleSubmit);
                                          }}
                                          className="m-0 p-3 mr-4 max-width-330"
                                          color="outline-red"
                                          size="lg"
                                          value={option}
                                          block
                                          type="button"
                                        >
                                          {option}
                                        </Button>
                                      ) : null))
                                    }
                                  </div>
                                </>
                              )
                            }
                          </FormGroup>
                        ))
                      }
                    </div>
                  ))
                }
                {
                  (!buttonQuestion || !errors[(buttonQuestion || {}).id]) && (
                    <div className="d-flex pt-3 mt-5 mb-5">
                      <Button
                        className="m-0 p-3 mr-4 max-width-180 issuer-button-outline"
                        color="outline-info"
                        size="lg"
                        block
                        type="button"
                        onClick={() => history.goBack()}
                      >
                        <Translate translationKey="Texts.go-back" />
                      </Button>
                      <LaddaButton
                        type="submit"
                        color="primary"
                        data-style={ZOOM_OUT}
                        loading={formikBag.isSubmitting}
                        disabled={!formikBag.isValid}
                        className="btn p-3 m-0 text-white issuer-button-primary btn-block max-width-330 font-16 font-weight-bold"
                        onClick={formikBag.handleSubmit}
                      >
                        <Translate translationKey="Texts.continue" />
                      </LaddaButton>
                    </div>
                  )
                }
              </div>
            </SecuritizePage>
          );
        }}
      </Formik>
    </SecuritizePage>
  );
};

InvestorQualification.defaultProps = {
  isError: false,
  isLoading: false,
  sections: [],
  getQualificationQuestions: () => undefined,
  submitQualificationQuestions: () => undefined,
  onSubmitSuccess: () => undefined,
  onSubmitError: () => undefined,
  showModal: () => undefined,
  user: {},
  pageNumber: 0,
};

InvestorQualification.propTypes = {
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  sections: PropTypes.arrayOf(PropTypes.any),
  getQualificationQuestions: PropTypes.func,
  submitQualificationQuestions: PropTypes.func,
  onSubmitSuccess: PropTypes.func,
  onSubmitError: PropTypes.func,
  showModal: PropTypes.func,
  user: PropTypes.objectOf(PropTypes.any),
  pageNumber: PropTypes.number,
  routesLength: PropTypes.number.isRequired,
};

const mapStateToProps = (state) => {
  const { investorQualification, auth } = state;
  return {
    isLoading: investorQualification.isLoading,
    isError: investorQualification.isError,
    sections: investorQualification.sections,
    user: auth.user,
  };
};

export default connect(
  mapStateToProps,
  {
    getQualificationQuestions,
    submitQualificationQuestions,
    showModal,
  },
)(InvestorQualification);
